import Anthropic from '@anthropic-ai/sdk';
import type { FormDocument, FieldOption, Field, Section } from '@fulcrum/form-embedded';

// ─── Tool definition ──────────────────────────────────────────────────────────
// Forced tool use (tool_choice.name) guarantees Claude returns structured JSON
// matching this schema — no markdown fence stripping or JSON.parse guesswork.
// Pattern mirrors Fulcrum.Schedule.Embedded's apply_schedule_change tool.

const PARSE_FORM_TOOL: Anthropic.Tool = {
  name: 'parse_form_screenshot',
  description: 'Output the form fields and sections visible in the screenshot as a structured FormDocument.',
  input_schema: {
    type: 'object' as const,
    required: ['sections'],
    properties: {
      sections: {
        type: 'array',
        description: 'Top-level sections (pages/groups) of the form.',
        items: {
          type: 'object',
          required: ['id', 'name', 'order', 'fields'],
          properties: {
            id: { type: 'string', description: 'Short id: s1, s2, …' },
            name: { type: 'string', description: 'Internal name, no spaces.' },
            label: { type: 'string', description: 'Display heading shown above the section.' },
            description: { type: 'string', description: 'Optional sub-heading or instructions.' },
            order: { type: 'number' },
            fields: {
              type: 'array',
              items: {
                type: 'object',
                required: ['id', 'sectionId', 'label', 'handle', 'fieldType', 'order', 'isRequired', 'isHidden'],
                properties: {
                  id: { type: 'string', description: 'Globally unique: f1, f2, …' },
                  sectionId: { type: 'string', description: 'Must match the parent section id.' },
                  label: { type: 'string', description: 'Field label as shown in the form.' },
                  handle: { type: 'string', description: 'snake_case machine name, globally unique.' },
                  fieldType: {
                    type: 'string',
                    enum: ['TXT', 'TXA', 'NUM', 'DAT', 'CHK', 'RDO', 'DDL', 'MSL', 'SCALE', 'LABEL'],
                    description:
                      'TXT=text, TXA=textarea, NUM=number, DAT=date, CHK=checkbox, RDO=radio, DDL=dropdown, MSL=multi-select, SCALE=likert/rating, LABEL=static text.',
                  },
                  order: { type: 'number' },
                  isRequired: { type: 'boolean', description: 'True when the form shows an asterisk, "required", or bold label.' },
                  isHidden: { type: 'boolean', enum: [false] },
                  options: {
                    type: 'array',
                    description: 'Required for RDO, DDL, MSL, SCALE, and multi-option CHK.',
                    items: {
                      type: 'object',
                      required: ['id', 'value', 'label', 'order'],
                      properties: {
                        id: { type: 'string' },
                        value: { type: 'string' },
                        label: { type: 'string' },
                        order: { type: 'number' },
                      },
                    },
                  },
                  layout: {
                    type: 'object',
                    description: 'Omit for full-width. Use half+half (or third+twoThirds, etc.) for side-by-side fields.',
                    properties: {
                      width: { type: 'string', enum: ['full', 'twoThirds', 'half', 'third', 'quarter'] },
                    },
                    required: ['width'],
                  },
                },
              },
            },
          },
        },
      },
    },
  },
};

// ─── System prompt ────────────────────────────────────────────────────────────

const SYSTEM_PROMPT =
  'You are a form-analysis assistant. Analyze the screenshot and call parse_form_screenshot with every visible ' +
  'field and section. Infer field types from visual cues (input boxes → TXT/TXA/NUM/DAT, radio buttons → RDO, ' +
  'checkboxes → CHK, dropdowns → DDL, rating scales → SCALE, static headings/paragraphs → LABEL). ' +
  'Infer isRequired from asterisks or "required" labels. ' +
  'Infer layout width from side-by-side positioning (two equal columns → half+half, etc.). ' +
  'If the form has no clear sections, create one section with id "s1" and name "Main".';

// ─── File picker helper ───────────────────────────────────────────────────────

function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.oncancel = () => resolve(null);
    input.click();
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Strip "data:<type>;base64," prefix — Anthropic API expects raw base64.
      resolve((reader.result as string).split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Claude vision call ───────────────────────────────────────────────────────
// Calls the Anthropic API directly from the browser via VITE_ANTHROPIC_API_KEY.
// Fine for demos and internal tools; production deployments should proxy through
// a backend. Pattern mirrors Fulcrum.Schedule.Embedded's handleAiMessage().

export async function importFormFromScreenshot(): Promise<FormDocument | null> {
  const file = await pickImageFile();
  if (!file) return null;

  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY as string | undefined;
  if (!apiKey) {
    throw new Error(
      'VITE_ANTHROPIC_API_KEY is not set. Add it to packages/host/.env to enable screenshot import.',
    );
  }

  const base64 = await fileToBase64(file);
  const mediaType = file.type as 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });

  const response = await client.messages.create({
    model: 'claude-opus-4-8',
    // 16 k gives enough headroom for forms with many fields and choice options.
    // The PCL-5, C-SSRS, and combined PHQ-9+GAD-7 can each exceed 4 k tokens of output.
    max_tokens: 16384,
    system: SYSTEM_PROMPT,
    tools: [PARSE_FORM_TOOL],
    // Forced tool use: Claude must call parse_form_screenshot — no free-form text,
    // no markdown fences, guaranteed structured output.
    tool_choice: { type: 'tool', name: 'parse_form_screenshot' },
    messages: [
      {
        role: 'user',
        content: [
          {
            type: 'image',
            source: { type: 'base64', media_type: mediaType, data: base64 },
          },
          { type: 'text', text: 'Parse this form screenshot.' },
        ],
      },
    ],
  });

  if (response.stop_reason === 'max_tokens') {
    throw new Error(
      'The response was cut off before Claude finished generating the form (max_tokens reached). ' +
      'Try screenshotting fewer pages at once, or crop to just the form area to reduce output size.',
    );
  }

  const toolBlock = response.content.find((b) => b.type === 'tool_use');
  if (!toolBlock || toolBlock.type !== 'tool_use') {
    throw new Error('Claude did not return a tool_use block — unexpected response format.');
  }

  const input = toolBlock.input as { sections: Array<Section & { fields: Field[] }> };
  if (!input.sections?.length) {
    throw new Error('Claude returned an empty form — no sections were parsed from the screenshot.');
  }
  return { sections: input.sections } satisfies FormDocument;
}
